// The customer's PO on the invoice screen. The PO lives on the pipeline record
// (that is what the printed invoice, the challan and Dispatch all read — see
// src/shared/documents/fill-bill-to-gaps.js), so this edits the record the
// invoice came from rather than keeping a second copy on the invoice. It used
// to be captured only in the "Convert to Sales Order" dialog, which the
// invoice flow no longer shows (the sales order is created automatically).

import { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import { Button } from '@/shared/components/ui/Button';
import { usePipelineRecord } from '@/features/pipeline/hooks/usePipeline';
import {
  CustomerPoFields, customerPoFromRecord, type CustomerPoValue,
} from '@/features/pipeline/components/CustomerPoFields';

// A quotation raised straight from Quotations has no pipeline record; then the
// PO is kept on that quotation itself (PUT /api/sales-ops/orders/:id/customer-po),
// which is where the printed invoice reads it from. When both deal and order exist,
// we sync both so the PO reflects across the entire Quotation -> PI -> Invoice chain.
export function InvoicePoPanel({ recordId, orderId }: { recordId?: string; orderId?: string }) {
  const qc = useQueryClient();
  const { data: pipelineRecord, isLoading: recordLoading } = usePipelineRecord(recordId || '');
  const { data: order, isLoading: orderLoading } = useQuery({
    queryKey: ['sales-order', orderId, 'customer-po'],
    queryFn: async () => { const { data } = await api.get(`/api/sales-ops/orders/${orderId}`); return (data?.order || data) as Record<string, unknown>; },
    enabled: !!orderId,
  });

  const record = useMemo(() => {
    const o = order as Record<string, unknown> | undefined;
    const pr = pipelineRecord as Record<string, unknown> | undefined;
    if (!pr && !o) return null;
    return {
      ...(pr || {}),
      ...(o || {}),
      customerPoNumber: o?.customerPoNumber || pr?.customerPoNumber || '',
      customerPoDate: o?.customerPoDate || pr?.customerPoDate || null,
      customerPoAttachments: (o?.customerPoAttachments as unknown[])?.length ? o?.customerPoAttachments : pr?.customerPoAttachments || [],
      hasCustomerPo: Boolean(
        o?.customerPoNumber || pr?.customerPoNumber ||
        (o?.customerPoAttachments as unknown[])?.length || (pr?.customerPoAttachments as unknown[])?.length
      ),
    };
  }, [pipelineRecord, order]);

  const isLoading = (!!recordId && recordLoading) || (!!orderId && orderLoading);
  const [draft, setDraft] = useState<CustomerPoValue | null>(null);
  const [saving, setSaving] = useState(false);
  if (isLoading || !record) return null;
  const value = draft ?? customerPoFromRecord(record as unknown as Record<string, unknown>);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...value, customerPoDate: value.customerPoDate || null };
      const promises: Promise<unknown>[] = [];
      if (orderId) {
        promises.push(api.put(`/api/sales-ops/orders/${orderId}/customer-po`, payload));
      }
      if (recordId) {
        promises.push(api.patch(`/api/pipeline/${recordId}`, payload));
      }
      await Promise.all(promises);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['pipeline'] }),
        qc.invalidateQueries({ queryKey: ['sales-order'] }),
        qc.invalidateQueries({ queryKey: ['sales-doc'] }),
        qc.invalidateQueries({ queryKey: ['quotations'] }),
        qc.invalidateQueries({ queryKey: ['proforma'] }),
        qc.invalidateQueries({ queryKey: ['proformas'] }),
        qc.invalidateQueries({ queryKey: ['dispatch'] }),
      ]);
      setDraft(null);
      toast.success('PO details saved — synced across Quotation, Proforma & Invoices.');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not save the PO details.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <CustomerPoFields value={value} onChange={setDraft} />
      {draft && (
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={() => setDraft(null)} disabled={saving}>Discard</Button>
          <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save PO Details'}</Button>
        </div>
      )}
    </div>
  );
}
